import { useRef, useState } from "react";
import { Card, Pill } from "../components/ui";

type IngestResult = {
  ok: boolean;
  ingestId?: number;
  written?: number;
  rejected?: number;
  unresolved?: string[];
  detail?: unknown;
  error?: string;
};

type Item = { key: string; file: File; status: "queued" | "uploading" | "done"; result: IngestResult | null };

export default function BringInScreen() {
  const [dragOver, setDragOver] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  // Uploads run ONE AT A TIME on purpose. The server parses each file into D1
  // as it lands, and three rota PDFs posted together were all rejected on
  // 2026-09-15 ("detached ArrayBuffer"); the same three sent one after another
  // all imported. A queue keeps the multi-select convenience without the race.
  const pending = useRef<Item[]>([]);
  const draining = useRef(false);

  function patch(key: string, changes: Partial<Item>) {
    setItems((list) => list.map((it) => (it.key === key ? { ...it, ...changes } : it)));
  }

  async function uploadOne(it: Item): Promise<void> {
    patch(it.key, { status: "uploading" });
    let result: IngestResult;
    try {
      const form = new FormData();
      form.append("file", it.file);
      const res = await fetch("/api/admin/ingest", { method: "POST", body: form });
      result = (await res.json()) as IngestResult;
    } catch (e: any) {
      result = { ok: false, error: String(e.message ?? e) };
    }
    patch(it.key, { status: "done", result });
  }

  // A second drop while the first is still importing just joins the queue.
  async function drain() {
    if (draining.current) return;
    draining.current = true;
    try {
      while (pending.current.length) await uploadOne(pending.current.shift()!);
    } finally {
      draining.current = false;
    }
  }

  function add(files: FileList | File[] | null | undefined) {
    const picked = Array.from(files ?? []);
    if (!picked.length) return;
    const fresh: Item[] = picked.map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`, file, status: "queued", result: null,
    }));
    setItems((list) => [...fresh, ...list]);
    pending.current.push(...fresh);
    void drain();
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    add(e.dataTransfer.files);
  }

  const busy = items.some((it) => it.status !== "done");
  const doneCount = items.filter((it) => it.status === "done").length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl text-ink">Bring in data</h1>
        <span className="text-sm text-neutral-400">for anything the apps and emails do not collect yet</span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 lg:grid-cols-[1.5fr_1fr]">
        <div className="flex flex-col gap-3.5">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-card border-2 border-dashed px-6 py-10 text-center ${
              dragOver ? "border-brand bg-red-50/40" : "border-neutral-300 bg-white"
            }`}
          >
            <b className="text-ink">Drop anything</b> <span className="text-neutral-500">· xlsx, csv, pdf · one file or many</span>
            <p className="mt-1.5 text-sm text-neutral-400">
              Core works out what each one is from its shape, and asks if unsure
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".xlsx,.csv,.pdf,.xls"
              className="hidden"
              onChange={(e) => {
                add(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {items.length > 1 && (
            <p className="text-sm text-neutral-500">
              {busy ? `Importing ${doneCount + 1} of ${items.length}…` : `${items.length} files imported`}
              {" · "}
              {items.filter((it) => it.result?.ok).length} ok, {items.filter((it) => it.result && !it.result.ok).length} failed
            </p>
          )}

          {items.map((it) => (
            <FileResult key={it.key} item={it} />
          ))}
        </div>

        <Card>
          <h2 className="mb-3 text-lg">Other ways in</h2>
          <table className="w-full text-[13px]">
            <tbody>
              <Row title="Email · data@core.gailshub.uk" sub="forward any report; the parsers pick it up" status="on" />
              <Row title="Apps · Forecasts, Overview, Shifts" sub="write API, service binding" status="on" />
              <Row title="Rota PDF via the Overview form" sub="sites attach it on Sunday" status="on" />
              <Row title="Retry queue" sub="see Flows for the latest failures" status="amber" statusLabel="check Flows" />
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

function FileResult({ item }: { item: Item }) {
  const { file, status, result } = item;
  if (status !== "done" || !result) {
    return (
      <Card>
        <span className="text-neutral-500">{status === "uploading" ? "Importing" : "Waiting"} · {file.name}</span>
      </Card>
    );
  }
  const detail = (result.detail ?? {}) as { sites?: number; totalSites?: number; duplicates?: number };
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-lg">
          {result.ok ? "Imported" : "Could not import"} · {file.name}
        </h2>
        <Pill tone={result.ok ? "ok" : "red"}>{result.ok ? "ok" : "failed"}</Pill>
      </div>
      {result.ok ? (
        <div className="grid grid-cols-2 gap-4 text-center lg:grid-cols-4">
          <Ring value={String(result.written ?? 0)} label="rows written" tone="ok" />
          <Ring
            value={detail.totalSites ? `${detail.sites ?? 0}/${detail.totalSites}` : String(detail.sites ?? "—")}
            label="sites matched"
            tone="ok"
          />
          <Ring value={String(result.rejected ?? 0)} label="rejected" tone={result.rejected ? "amber" : "ok"} />
          <Ring value={String(result.unresolved?.length ?? 0)} label="unresolved names" tone={result.unresolved?.length ? "amber" : "ok"} />
        </div>
      ) : (
        <p className="text-sm text-brand">{result.error}</p>
      )}
      {!!result.unresolved?.length && (
        <p className="mt-3.5 text-xs text-neutral-500">
          Unresolved: {result.unresolved.join(", ")} — resolve them on the Sites screen.
        </p>
      )}
    </Card>
  );
}

function Ring({ value, label, tone }: { value: string; label: string; tone: "ok" | "amber" }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className={`flex h-16 w-16 items-center justify-center rounded-full border-4 text-sm font-semibold ${
          tone === "ok" ? "border-green-500 text-green-700" : "border-amber-500 text-amber-700"
        }`}
      >
        {value}
      </div>
      <span className="text-xs text-neutral-500">{label}</span>
    </div>
  );
}

function Row({ title, sub, status, statusLabel }: { title: string; sub: string; status: "on" | "amber"; statusLabel?: string }) {
  return (
    <tr>
      <td className="border-b border-neutral-100 py-2.5 text-ink">
        {title}
        <small className="block text-neutral-400">{sub}</small>
      </td>
      <td className="border-b border-neutral-100 py-2.5 text-right">
        <Pill tone={status === "on" ? "ok" : "amber"}>{statusLabel ?? status}</Pill>
      </td>
    </tr>
  );
}
